import React, { useMemo } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { CustomTooltip } from "@pages/Dashboard/features/SalesPerformance/Chart/CustomTooltip";
import { WidgetClass } from "@services/widget.service";
import { ChartDataPoint } from "@services/sales-performance/sales-performance.types";
import { useIsMobile } from "src/utils/isMobile";
import { modifyTooltipPayload } from "@components/SalesPerformance/utils";

interface ColumnChartProps {
  dataSet: ChartDataPoint[];
  setActiveLabel?: React.Dispatch<React.SetStateAction<string | null>>;
  metrics: string[];
  metricsColors: { [key: string]: string };
  isForecasted: boolean;
}

interface TooltipContentProps extends TooltipProps<number, string> {
  metricsColors: { [key: string]: string };
}

const BarTooltipContent: React.FC<TooltipContentProps> = ({
  active,
  payload,
  label,
  metricsColors,
}) => {
  if (!active || !payload) return null;
  const modified = modifyTooltipPayload(payload);
  return (
    <CustomTooltip
      active={active}
      payload={modified}
      colorMetric={metricsColors}
      label={label}
    />
  );
};

interface ChartDimensions {
  margin: { top: number; right: number; left: number; bottom: number };
  tickPadding: { left: number; right: number };
  height: number;
  xAxisDy: number;
}

const getResponsiveDimensions = (isMobile: boolean): ChartDimensions => ({
  margin: isMobile
    ? { top: 20, right: -25, left: -25, bottom: 20 }
    : { top: 20, right: 30, left: 20, bottom: 14 },
  tickPadding: {
    left: isMobile ? 10 : 0,
    right: isMobile ? -10 : 0,
  },
  height: isMobile ? 250 : 350,
  xAxisDy: isMobile ? 5 : 10,
});

export const BarChart: React.FC<ColumnChartProps> = ({
  dataSet,
  metrics,
  metricsColors,
  isForecasted,
}) => {
  const widgetClass = new WidgetClass();
  const calculatedBarSize = Math.max(10, 60 / metrics.length);
  const isMobile = useIsMobile();
  const dimensions = useMemo(
    () => getResponsiveDimensions(isMobile),
    [isMobile],
  );

  const boundaryIndex = isForecasted
    ? Math.floor(dataSet.length / 2)
    : dataSet.length;

  const processedData = useMemo(() => {
    return dataSet.map((d, index) => {
      const newObj: any = { ...d };
      metrics.forEach((metric) => {
        newObj[`${metric}_main`] = index < boundaryIndex ? d[metric] : 0;
        newObj[`${metric}_forecast`] = index >= boundaryIndex ? d[metric] : 0;
      });
      return newObj;
    });
  }, [dataSet, metrics, boundaryIndex]);

  const xAxisInterval = useMemo(() => {
    if (!dataSet || dataSet.length === 0) {
      return 0;
    }

    try {
      const firstDateStr = dataSet[0]?.date;
      const lastDateStr = dataSet[dataSet.length - 1]?.date;

      if (!firstDateStr || !lastDateStr) return 0;

      const firstDate = new Date(firstDateStr);
      const lastDate = new Date(lastDateStr);

      const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (isMobile) {
        if (diffDays <= 7) return 0;
        if (diffDays <= 31) return 1;
        if (diffDays <= 90) return 3;

        return "preserveStartEnd";
      } else {
        if (diffDays <= 7) return 0;
        if (diffDays <= 31) return 1;
        if (diffDays <= 90) return 6;
        if (diffDays <= 182) return 13;
        if (diffDays <= 365) return 29;
        return 30;
      }
    } catch (error) {
      console.error("Error calculating date interval:", error);
      return isMobile ? "preserveStartEnd" : 1;
    }
  }, [dataSet, isMobile]);

  return (
    <ResponsiveContainer width="100%" height={dimensions.height}>
      <RechartsBarChart
        data={processedData}
        margin={dimensions.margin}
        barCategoryGap="20%"
      >
        <CartesianGrid
          stroke="#1F1F20"
          vertical={false}
          horizontalCoordinatesGenerator={(props) => {
            const { height } = props;
            return Array.from(
              { length: isMobile ? 5 : 8 },
              (_, i) => height * (i / (isMobile ? 4 : 8)),
            );
          }}
        />
        <XAxis
          dataKey="date"
          interval={xAxisInterval}
          tick={{
            fill: "#FFFFFF",
            dy: dimensions.xAxisDy,
            fontSize: isMobile ? 10 : 12,
          }}
          tickFormatter={(value) => {
            try {
              const parts = value.split(" ");
              if (parts.length === 2) {
                const day = parts[0];
                const month = parts[1];
                return `${month} ${day}`;
              }
              return value;
            } catch (e) {
              console.error("Error formatting XAxis tick:", e);
              return value;
            }
          }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          content={<BarTooltipContent metricsColors={metricsColors} />}
          cursor={false}
        />

        {metrics.map((metric, index) => {
          const yAxisId = metric;
          const orientation = index % 2 === 0 ? "left" : "right";
          const tickColor = metricsColors[metric];
          const tickFormatter = (value: number) =>
            widgetClass.formatValue(metric, value);

          return (
            <React.Fragment key={metric}>
              <YAxis
                yAxisId={yAxisId}
                orientation={orientation}
                stroke={tickColor}
                tickFormatter={tickFormatter}
                axisLine={false}
                tickLine={false}
                domain={["auto", "auto"]}
                tickCount={isMobile ? 5 : 11}
                tick={{
                  fill: tickColor,
                  dx: dimensions.tickPadding[orientation],
                  fontSize: isMobile ? 10 : 12,
                }}
                width={isMobile ? 50 : 60}
              />

              <Bar
                dataKey={`${metric}_main`}
                yAxisId={metric}
                fill={metricsColors[metric]}
                fillOpacity={1}
                barSize={calculatedBarSize}
              />

              {isForecasted && (
                <Bar
                  dataKey={`${metric}_forecast`}
                  yAxisId={metric}
                  fill={metricsColors[metric]}
                  fillOpacity={0.5}
                  stroke={metricsColors[metric]}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  barSize={calculatedBarSize}
                />
              )}
            </React.Fragment>
          );
        })}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};
